import React, { useState } from "react";
import { Dropdown, Nav } from "react-bootstrap";

import { useMeterManager } from "../../hooks/useMeterManager";
import { CustomerDetailsProps, CustomerInfoProps } from "../../pages/SalesDocuments-v2";
import {
  generateQuoteDocument,
  getDocumentSignerUrl,
} from "../../services/documentGeneration.service";
import { DocType } from "../../types/salesDocuments.types";

import MeterForm from "./meterForm";
import MeterModals from "./meterModals";
import { fuelTypes, getFuelTypeConfig } from "./salesDocuments.constants";

interface MeterContainerProps {
  customerZohoId: string;
  customerDetails: CustomerDetailsProps;
  customerInfo: CustomerInfoProps;
  companyName: string;
}

// FastAPI-style error body. `detail` is a list of field errors for a 422, a
// supplier-suggestion object for a 404, or a plain message otherwise
interface DocGenErrorResponse {
  detail?:
    | string
    | { loc: (string | number)[]; msg: string }[]
    | { did_you_mean?: string[]; message?: string };
  error?: string;
}

const MeterContainer: React.FC<MeterContainerProps> = ({
  customerZohoId,
  customerDetails,
  customerInfo,
  companyName,
}) => {
  const logic = useMeterManager(customerZohoId, companyName, customerDetails, customerInfo);
  const [iframeUrl, setIframeUrl] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<number | null>(null);
  const [docGenErrorMessage, setDocGenErrorMessage] = useState<string | null>(null);

  const handleRequest = async (docType: DocType) => {
    // Validation Logic
    const updated = logic.meters.map((m) => {
      const form = document.getElementById(`meter-${m.id.toString()}`) as HTMLFormElement | null;
      const formValid = form?.checkValidity() ?? true;
      const checkerValid = logic.runValidityChecker(m.id);
      const valid = formValid && checkerValid;
      if (!formValid) form?.classList.add("was-validated");
      return { ...m, hasError: !valid };
    });
    logic.setMeters(updated);
    const failed = updated.filter((m) => m.hasError).map((m) => m.id);
    logic.setFailedMeters(failed);

    if (failed.length > 0) {
      setTimeout(() => {
        document.getElementById("sales-doc-tabs")?.scrollIntoView({ behavior: "smooth" });
      }, 150);
      return;
    }

    // Request Logic
    setDocGenErrorMessage(null);
    logic.setIsGenerating(true);

    try {
      const response = await generateQuoteDocument(logic.buildPayload(docType));

      if (!response.ok) {
        const data: unknown = await response.json().catch(() => null);
        handleErrorResponse(response.status, data);
        return;
      }

      if (docType === "html") {
        const data = (await response.json()) as { raw_html: string };
        const blob = new Blob([data.raw_html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        setIframeUrl(url);
        setShowPreview(true);
        setDownloadUrl("");
      } else {
        const data = (await response.json()) as { doc_id: string };
        const signerResponse = await getDocumentSignerUrl(data.doc_id);
        if (!signerResponse.ok) {
          const data: unknown = await signerResponse.json().catch(() => null);
          handleErrorResponse(signerResponse.status, data);
          return;
        }
        const signerData = (await signerResponse.json()) as { url: string };
        setDownloadUrl(signerData.url);
      }
    } catch (err) {
      console.error("Generation failed:", err);
      handleErrorResponse(null, err);
    } finally {
      logic.setIsGenerating(false);
    }
  };

  // Request error handling
  const handleErrorResponse = (status: number | null, body: unknown) => {
    const data = body as DocGenErrorResponse | null;
    switch (status) {
      case 500:
        setDocGenErrorMessage("Server Error (500): Please report this to the dev team.");
        break;
      case 403:
        setDocGenErrorMessage(
          "You do not have the correct permissions to generate documents, contact the dev team to get your permissions updated.",
        );
        break;
      case 404: {
        const details404 =
          data?.detail && typeof data.detail === "object" && !Array.isArray(data.detail)
            ? data.detail
            : undefined;
        if (details404?.did_you_mean && details404.did_you_mean.length > 0) {
          setDocGenErrorMessage(
            `Supplier not found.\nDid you mean: ${details404.did_you_mean.join(", ")}?`,
          );
        } else if (data?.error) {
          setDocGenErrorMessage(data.error);
        } else {
          setDocGenErrorMessage(details404?.message ?? "Resource not found (404).");
        }
        break;
      }
      case 422: {
        const details422 = data?.detail;
        if (Array.isArray(details422)) {
          const msg = details422.map((d) => `${String(d.loc.at(-1))}: ${d.msg}`).join(", ");
          setDocGenErrorMessage(`Validation Error: ${msg}`);
        } else if (typeof details422 === "string") {
          setDocGenErrorMessage(details422);
        } else {
          setDocGenErrorMessage("Invalid input data.");
        }
        break;
      }
      default:
        setDocGenErrorMessage("An unexpected error occurred. Please check your connection.");
    }
  };

  return (
    <div id="sales-doc-tabs">
      <Nav variant="tabs">
        {logic.meters.map((m) => (
          <Nav.Item key={m.id}>
            <Nav.Link
              active={m.id === logic.activeMeter}
              onClick={() => {
                logic.setActiveMeter(m.id);
              }}
            >
              {getFuelTypeConfig(m.fuelType).emoji} {m.name} {m.hasError ? "⚠️" : ""}
            </Nav.Link>
          </Nav.Item>
        ))}
        <Dropdown as={Nav.Item}>
          <Dropdown.Toggle as={Nav.Link}>+ Add</Dropdown.Toggle>
          <Dropdown.Menu>
            {fuelTypes.map((f) => (
              <Dropdown.Item
                key={f.type}
                onClick={() => {
                  logic.handleAddMeter(f.type);
                }}
              >
                {f.emoji} Add {f.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Nav>

      {logic.failedMeters.length > 0 && logic.meters.length > 0 && (
        <div className="alert alert-danger mt-3 mb-3">
          <strong>Please fix errors in the following meters:</strong>
          <ul className="mb-0">
            {logic.failedMeters.map((id) => {
              const meter = logic.meters.find((m) => m.id === id);
              return <li key={id}>{meter?.name ?? `Meter ${id.toString()}`}</li>;
            })}
          </ul>
        </div>
      )}

      <div className="mx-auto">
        {logic.meters.map((m) => (
          <MeterForm
            key={m.id}
            meter={m}
            isActive={m.id === logic.activeMeter}
            isValidated={logic.validatedMeters.has(m.id)}
            registerPayloadGetter={logic.registerPayloadGetter}
            registerValidityChecker={logic.registerValidityChecker}
            onDelete={setDeleteCandidate}
            onNameChange={(id, name) => {
              logic.setMeters((ms) => ms.map((x) => (x.id === id ? { ...x, name } : x)));
            }}
          />
        ))}
        {logic.meters.length === 0 && (
          <div className="sales-doc-empty">
            <p>Add a meter to start creating a quote!</p>
          </div>
        )}
      </div>

      {logic.meters.length > 0 && (
        <div className="d-flex gap-2 mt-3 mb-5 justify-content-center">
          <button
            className="sales-doc-btn-secondary"
            onClick={() => {
              void handleRequest("html");
            }}
            disabled={logic.isGenerating}
          >
            {logic.isGenerating ? "..." : "Generate Preview"}
          </button>
          <button
            className="sales-doc-btn-secondary"
            onClick={() => {
              void handleRequest("pdf");
            }}
            disabled={logic.isGenerating}
          >
            {logic.isGenerating ? "..." : "Generate PDF"}
          </button>
          {iframeUrl && (
            <button
              className="sales-doc-btn-secondary"
              onClick={() => {
                setShowPreview(true);
              }}
            >
              👓 Open Preview
            </button>
          )}
          {downloadUrl && (
            <button
              className="sales-doc-btn-secondary"
              onClick={() => {
                window.location.href = downloadUrl;
              }}
            >
              ⬇️ Download
            </button>
          )}
        </div>
      )}

      {logic.isGenerating && (
        <div className="d-flex justify-content-center mt-2">
          <div className="spinner-border spinner-border-sm text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {docGenErrorMessage && (
        <div className="alert alert-danger mt-3 mb-3" style={{ whiteSpace: "pre-line" }}>
          <strong>Document generation error:</strong> {docGenErrorMessage}
          <button
            type="button"
            className="btn-close float-end"
            onClick={() => {
              setDocGenErrorMessage(null);
            }}
          ></button>
        </div>
      )}

      <MeterModals
        deleteCandidate={deleteCandidate}
        setDeleteCandidate={setDeleteCandidate}
        onConfirmDelete={(id) => {
          logic.handleDeleteMeter(id);
          setDeleteCandidate(null);
          setTimeout(() => {
            document.getElementById("sales-doc-tabs")?.scrollIntoView({ behavior: "smooth" });
          }, 150);
        }}
        showPreview={showPreview}
        setShowPreview={setShowPreview}
        iframeUrl={iframeUrl}
      />
    </div>
  );
};

export default MeterContainer;
